import { CSSProperties } from 'react';
import {
  accentBarStyle as accentBarStyleBase,
  animMs as animMsBase,
  bodyStyle as bodyStyleBase,
  contentIndentFor as contentIndentForBase,
  dividerStyle as dividerStyleBase,
  justifyFor,
  labelStyle as labelStyleBase,
  mutedStyle as mutedStyleBase,
  surfaceStyle as surfaceStyleBase,
  withAlpha,
} from '../cowork/styleUtils';
import { OverlayStyle } from '../cowork/types';
import { SubathonStyle } from './types';

/**
 * El extensible comparte el vocabulario visual de cowork (paneles, colores,
 * tipografías), así que reusamos esos helpers y acá agregamos solo lo propio
 * del contador: el reloj, los dígitos en caja y las barras.
 */
const shared = (style: SubathonStyle) => style as unknown as OverlayStyle;

export { justifyFor, withAlpha };

export const surfaceStyle = (style: SubathonStyle, extra: CSSProperties = {}) =>
  surfaceStyleBase(shared(style), extra);

export const accentBarStyle = (style: SubathonStyle) => accentBarStyleBase(shared(style));

export const contentIndentFor = (style: SubathonStyle) => contentIndentForBase(shared(style));

export const labelStyle = (style: SubathonStyle, extra: CSSProperties = {}) =>
  labelStyleBase(shared(style), extra);

export const bodyStyle = (style: SubathonStyle, extra: CSSProperties = {}) =>
  bodyStyleBase(shared(style), extra);

export const mutedStyle = (style: SubathonStyle, extra: CSSProperties = {}) =>
  mutedStyleBase(shared(style), extra);

export const dividerStyle = (style: SubathonStyle) => dividerStyleBase(shared(style));

export const animMs = (style: SubathonStyle, baseMs: number) => animMsBase(shared(style), baseMs);

/** Reloj del extensible: puede pasar de 24 horas, así que las horas no se limitan. */
export function formatSubathonClock(ms: number, showHours: boolean, showSeconds: boolean): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (!showHours) {
    const allMinutes = Math.floor(total / 60);
    return showSeconds
      ? `${allMinutes}:${String(seconds).padStart(2, '0')}`
      : String(Math.ceil(total / 60));
  }

  if (!showSeconds) return `${hours}:${String(minutes).padStart(2, '0')}`;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function formatSpan(ms: number): string {
  const negative = ms < 0;
  const totalMinutes = Math.round(Math.abs(ms) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const text = hours > 0
    ? (minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`)
    : `${minutes} min`;
  return negative ? `-${text}` : text;
}

export function formatSeconds(seconds: number): string {
  const abs = Math.abs(seconds);
  if (abs < 60) return `${seconds} seg`;
  return formatSpan(seconds * 1000);
}

/** Tipografía del reloj, con el color de apuro cuando queda poco. */
export function clockStyle(style: SubathonStyle, urgent: boolean): CSSProperties {
  const align = style.timerAlign === 'inherit' ? style.align : style.timerAlign;

  return {
    fontFamily: style.fontFamily,
    fontSize: style.timerFontSize,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    lineHeight: 1,
    color: urgent ? style.urgentColor : style.textColor,
    fontVariantNumeric: style.monoDigits ? 'tabular-nums' : undefined,
    textShadow: style.textShadow ? '0 3px 14px rgba(0, 0, 0, 0.7)' : undefined,
    alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
    transform:
      style.timerOffsetX || style.timerOffsetY
        ? `translate(${style.timerOffsetX || 0}px, ${style.timerOffsetY || 0}px)`
        : undefined,
    whiteSpace: 'nowrap',
  };
}

/** Caja opcional alrededor del reloj entero. */
export function clockBoxStyle(style: SubathonStyle): CSSProperties | undefined {
  if (!style.timerBoxed) return undefined;
  return {
    padding: `${Math.round(style.padding * 0.3)}px ${Math.round(style.padding * 0.55)}px`,
    borderRadius: style.panelShape === 'square' ? 0 : Math.max(8, Math.round(style.borderRadius * 0.7)),
    background: withAlpha(style.textColor, 0.08),
    border: `1px solid ${withAlpha(style.accentColor, 0.3)}`,
  };
}

/** Un recuadro por dígito, como un marcador de estadio. */
export function digitBoxStyle(style: SubathonStyle): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: Math.round(style.timerFontSize * 0.68),
    padding: `${Math.round(style.timerFontSize * 0.1)}px ${Math.round(style.timerFontSize * 0.06)}px`,
    borderRadius: style.panelShape === 'square' ? 0 : Math.max(6, Math.round(style.borderRadius * 0.45)),
    background: withAlpha(style.accentColor, 0.16),
    border: `1px solid ${withAlpha(style.accentColor, 0.34)}`,
  };
}

/** Barra de avance del panel (tope o progreso). */
export function barStyle(style: SubathonStyle, ratio: number): { track: CSSProperties; fill: CSSProperties } {
  const thickness = Math.max(2, style.barThickness || 6);
  const clamped = Math.max(0, Math.min(1, ratio));

  return {
    track: {
      alignSelf: 'stretch',
      height: thickness,
      borderRadius: style.panelShape === 'square' ? 0 : 999,
      background: withAlpha(style.textColor, 0.14),
      overflow: 'hidden',
      flexShrink: 0,
    },
    fill: {
      width: `${clamped * 100}%`,
      height: '100%',
      borderRadius: 'inherit',
      background:
        style.barStyle === 'flat'
          ? style.accentColor
          : `linear-gradient(90deg, ${style.secondaryColor}, ${style.accentColor})`,
      boxShadow: style.barStyle === 'glow' ? `0 0 ${thickness * 2}px ${withAlpha(style.accentColor, 0.7)}` : undefined,
      transition: style.animationsEnabled ? 'width 0.6s ease' : undefined,
    },
  };
}
